import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, CalendarPlus, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import {
  FOLLOWUP_PRIORITY_CHOICES,
  followupDueDays,
  type FollowupPriority,
} from '../../utils/followupPriority';

interface ScheduleFollowupModalProps {
  isOpen: boolean;
  onClose: () => void;
  posId: string;
  posName: string;
  storeType: string;
  onComplete: () => void;
}

const MATERIALS = [
  { id: 'pricetags', label: 'Pricetags' },
  { id: 'wobblers', label: 'Wobblers' },
  { id: 'posters', label: 'Posters' },
  { id: 'flyers', label: 'Flyers' },
  { id: 'merch', label: 'Merch' },
];

const ACTIONS = [
  { id: 'meet_decision_maker', label: 'Meet Decision Maker' },
  { id: 'install_pos', label: 'Install POS Material' },
  { id: 'check_competitors', label: 'Check Competitor Activity' },
  { id: 'negotiate_placement', label: 'Negotiate Better Placement' },
  { id: 'photo_documentation', label: 'Photo Documentation' },
];

export const ScheduleFollowupModal: React.FC<ScheduleFollowupModalProps> = ({
  isOpen,
  onClose,
  posId,
  posName,
  storeType,
  onComplete,
}) => {
  const [priority, setPriority] = useState<FollowupPriority>('medium');
  const [materials, setMaterials] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleMaterial = (id: string) => {
    if (materials.includes(id)) {
      setMaterials(materials.filter(m => m !== id));
    } else {
      setMaterials([...materials, id]);
    }
  };

  const toggleAction = (id: string) => {
    if (actions.includes(id)) {
      setActions(actions.filter(a => a !== id));
    } else {
      setActions([...actions, id]);
    }
  };

  const handleSubmit = async () => {
    // Validate at least one material or action
    if (materials.length === 0 && actions.length === 0) {
      toast.error('Select at least one material or action');
      return;
    }

    setLoading(true);
    try {
      await api.post('/visits/schedule-followup', {
        pos_id: posId,
        store_type: storeType,
        followup_materials: materials,
        followup_actions: actions,
        followup_notes: notes || undefined,
        followup_priority: priority,
      });

      toast.success('Follow-up scheduled!');
      
      // Reset form
      setPriority('medium');
      setMaterials([]);
      setActions([]);
      setNotes('');
      
      onComplete();
    } catch (error) {
      console.error('Failed to schedule follow-up:', error);
      toast.error('Failed to schedule follow-up');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getDueDateText = () => {
    const days = followupDueDays(priority);
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + days);
    return dueDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <CalendarPlus className="text-blue-600" size={28} />
            <div>
              <h2 className="text-xl font-black text-gray-900">Schedule Follow-up</h2>
              <p className="text-sm text-gray-600">{posName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Priority Selector */}
          <div>
            <label className="block font-bold text-gray-900 mb-3">Priority</label>
            <div className="flex gap-2">
              {FOLLOWUP_PRIORITY_CHOICES.map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  onClick={() => setPriority(choice.value)}
                  className={`flex-1 px-2 py-3 rounded-lg font-bold text-white text-xs transition-all ${
                    priority === choice.value
                      ? `${choice.activeClass} ring-4 ring-offset-2 ${choice.ring}`
                      : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                  }`}
                >
                  {choice.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Due: <strong>{getDueDateText()}</strong> ({followupDueDays(priority)} days)
            </p>
          </div>

          {/* Materials to Provide */}
          <div>
            <label className="block font-bold text-gray-900 mb-3">Materials Needed</label>
            <div className="grid grid-cols-2 gap-2">
              {MATERIALS.map(mat => (
                <label 
                  key={mat.id} 
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    materials.includes(mat.id)
                      ? 'border-purple-400 bg-purple-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={materials.includes(mat.id)}
                    onChange={() => toggleMaterial(mat.id)}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="font-medium text-gray-700">{mat.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions Required */}
          <div>
            <label className="block font-bold text-gray-900 mb-3">Actions Required</label>
            <div className="grid grid-cols-1 gap-2">
              {ACTIONS.map(action => (
                <label 
                  key={action.id} 
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    actions.includes(action.id)
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={actions.includes(action.id)}
                    onChange={() => toggleAction(action.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-700">{action.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block font-bold text-gray-900 mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              rows={3}
              placeholder="Additional context or instructions..."
            />
          </div>

          {/* Validation message */}
          {materials.length === 0 && actions.length === 0 && (
            <p className="text-sm text-orange-600 font-medium">
              Select at least one material or action to schedule.
            </p>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={loading || (materials.length === 0 && actions.length === 0)}
            >
              {loading ? 'Scheduling...' : (
                <>
                  <CheckCircle size={18} />
                  Schedule Follow-up
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
